import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from "@angular/core";
import {EntityData, provideSearchModel} from "@fedelta-pos/data-client";
import {GeolocateService} from "../../store/geolocate";
import {SessionModel} from "../../orchestration/session.model";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {Page, PageGeometry} from "../../orchestration/page";
import {NavigationService} from "../../orchestration/navigation.service";
import {LocationSearchCoordinates} from "../location-search-panel/location-search-panel.component";
import {DeliveryDetails, OrderingContext, OrderingContextIntention, TableSelection} from "../ordering-context";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {NotificationService} from "../../notification/notification.service";
import {QrCodeReaderService, ScannerStatus} from "../../qr-code-reader/qr-code-reader.service";
import {ChewzieTableLookupService, TableLookupResponse} from "../chewzie-table-lookup.service";
import {ParamConfig, ParamConfigService} from "../../config-support/param-config.service";
import {NativeWaiterAppService} from "../../native-waiter-app.service";
import {MessageDetails, MessageLevel} from "../../notification/message/message";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {IdentityModel} from "../../identity/identity.model";
import {OrderingContextModel, OrderingContextStoreSearchFilterProvider} from "../ordering-context.model";
import {FormComponent} from "../../inputs/form/form.component";
import {NavigationParams} from "../../orchestration/navigation-params";
import {StringUtils} from "../../string-utils/string-utils";
import {BeaconData, BeaconOrderType, BeaconSearchModel} from "../../beacon/beacon-search.model";
import {TableLookupMethod} from "../../orchestration/app-entity.model";
import {
    IntegratedDeliveryAddressValidationRequestData,
    IntegratedDeliveryAddressValidationService
} from "../integrated-delivery-address-validation.service";
import {StoreData, StoreSearchModel, TableCodeBarcodeData} from "../../store/store-search.model";
import Geocoder = google.maps.Geocoder;
import GeocoderStatus = google.maps.GeocoderStatus;
import GeocoderResult = google.maps.GeocoderResult;
import GeocoderAddressComponent = google.maps.GeocoderAddressComponent;
import {AddressData} from "../../customer-info/address.data";
import {OrderType} from "../../order/order";

@Component({
    selector: "woa-ordering-context-selection-page",
    templateUrl: "./ordering-context-selection-page.component.html",
    providers: [
        provideSearchModel(StoreSearchModel),
        QrCodeReaderService,
        ChewzieTableLookupService,
        {provide: Page, useExisting: OrderingContextSelectionPageComponent}
    ]
})
export class OrderingContextSelectionPageComponent extends Page implements OnInit, AfterViewInit {
    static readonly deliveryEntrySelectionGeometry: PageGeometry = {
        height: "650px"
    };
    static readonly desktopOrderTypeSelectionGeometry: PageGeometry = {
        height: "520px"
    };
    static readonly desktopStoreSelectionGeometry: PageGeometry = {
        pillarboxedOnDesktop: "none",
        width: "850px",
        height: "750px"
    };
    private static readonly desktopTableScannerGeometry: PageGeometry = {
        pillarboxedOnDesktop: "none",
        width: "600px",
        height: "600px"
    };
    private static readonly mobileTableScannerGeometry: PageGeometry = {
        height: "450px"
    };

    static readonly stateNames: string[][] = [
        ["act", "australian capital territory"],
        ["nsw", "new south wales"],
        ["nt", "northern territory"],
        ["qld", "queensland"],
        ["sa", "south australia"],
        ["tas", "tasmania"],
        ["vic", "victoria"],
        ["wa", "western australia"]
    ];

    @ViewChild(FormComponent) public form: FormComponent;
    @ViewChild("containerQrCode") containerQrCode: ElementRef;

    OrderType = OrderType;
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    TableLookupMethod = TableLookupMethod;
    MessageLevel = MessageLevel;
    IconButtonType = IconButtonType;

    orderingContext: OrderingContext;
    loadingFailed: boolean = false;
    isLoading: boolean = false;
    hasLoaded: boolean;
    showAllStores: boolean = false;
    position: Position;
    location: LocationSearchCoordinates;
    canOrderAtTable: boolean;
    canOrderAhead: boolean;
    canOrderDelivery: boolean;
    orderType: OrderType;

    treatDeliveryAddressAsUnconfirmed: boolean = false;
    editingDeliveryAddress: boolean = false;
    addressError: string;
    addressErrorStore: StoreData;

    street: string;
    city: string;
    state: string;
    postCode: string;
    deliveryInstructions: string;
    orderMobilePhone: string;
    saveForMember: boolean;
    hasExistingAddress: boolean;
    addressMatchesMember: boolean;
    saveForMemberSpecified: boolean = false;

    selectingTable: boolean = false;
    // If a table number relates to a single store (restaurant):
    selectedStoreForTable: StoreData;
    // If a table number relates to multiple stores (food court) (and to carry other flags provided by table lookup service):
    selectedTable: TableSelection;
    tableLookupCode: string;
    tableLookupCodeMsg: string;
    tableCodeScannerMsg: string;
    tableCodeScannerExists: boolean;
    tableCodeScanning: boolean;
    tableCodeScannerChosen: boolean;
    tableCodeScannerAuthorised: boolean;
    tableCodeScannerDenied: boolean;
    tableSelectionMethod: TableLookupMethod = TableLookupMethod.MANUAL;
    isNativeCamera: boolean;
    canOpenSettings: boolean;

    manualTableCode: string;

    private orderAheadStoreOption: StoreData;
    private orderDeliveryStoreOption: StoreData;
    private orderAtTableStoreOption: StoreData;
    private tableCodeBarcodes: TableCodeBarcodeData[] = [];
    private storeOptionsChecked: boolean = false;
    private canFilterByState: boolean;
    private storeOrderTypeSelectionRequired: boolean;
    private orderTypeSelectionRequired: boolean;
    private geocoder: Geocoder; // Google service to get the geolocation of a place

    constructor(navigationParams: NavigationParams,
                private elementRef: ElementRef,
                private navigationService: NavigationService,
                private notificationService: NotificationService,
                private changeDetectorRef: ChangeDetectorRef,
                private geolocateService: GeolocateService,
                private loadingService: LoadingService,
                private integratedDeliveryAddressValidationService: IntegratedDeliveryAddressValidationService,
                private qrCodeReaderService: QrCodeReaderService,
                private tableLookupService: ChewzieTableLookupService,
                private beaconSearchModel: BeaconSearchModel,
                private paramConfig: ParamConfigService,
                private nativeWaiterAppService: NativeWaiterAppService,
                public identityModel: IdentityModel,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public responsiveService: ResponsiveService,
                public storeSearchModel: StoreSearchModel) {
        super();
        if (!this.orderingContextModel.disableLocationServices) {
            (async () => this.position = await this.geolocateService.getCurrentLocation(5000))();
        }

        this.orderingContext = navigationParams.get("orderingContext");
        if (this.orderingContext.selectedOrderType === OrderType.ORDER_AHEAD) {
            this.orderType = OrderType.ORDER_AHEAD;
        } else if (this.orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) {
            this.orderType = OrderType.ORDER_DELIVERY;
        } else if (this.orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) {
            this.orderType = OrderType.ORDER_AT_TABLE;
        }

        // noinspection JSIgnoredPromiseFromCall
        this.chooseTableCodeScanner();
        this.tableCodeScanning = false;
        this.tableCodeScannerAuthorised = false;
        this.tableCodeScannerDenied = false;
    }

    ngOnInit(): void {
        this.load();
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.showTableSelection) {
            await this.initTableCodeScanner();
        }
    }

    onDidDismissPage(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.destroyTableCodeScanner();
    }

    get customGeometry(): PageGeometry {
        return this.showDeliveryAddressEntry && this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ? OrderingContextSelectionPageComponent.deliveryEntrySelectionGeometry
            : this.showTableSelection ? (
                    !this.tableCodeScannerExists || !this.tableCodeScanning ? undefined
                        : this.responsiveService.mobile ? OrderingContextSelectionPageComponent.mobileTableScannerGeometry
                            : OrderingContextSelectionPageComponent.desktopTableScannerGeometry
                )
                : this.responsiveService.mobile ? undefined
                    : this.storeOptionsChecked && !this.showOrderTypeSelection ? OrderingContextSelectionPageComponent.desktopStoreSelectionGeometry
                        : OrderingContextSelectionPageComponent.desktopOrderTypeSelectionGeometry;
    }

    get isViewIntention(): boolean {
        return this.orderingContext.intention === OrderingContextIntention.VIEW_STORES ||
            this.orderingContext.intention === OrderingContextIntention.VIEW_CURRENT_STORE;
    }

    get title(): string {
        return this.isLoading ? ""
            : this.showTableSelection ? (
                    !this.tableCodeScanning ? "woa.heading.ordering-context.selectTable.query"
                        : this.orderingContext.selectedStore ? "woa.heading.ordering-context.scanTable.withStore"
                            : "woa.heading.ordering-context.scanTable.noStore")
                : this.showOrderTypeSelection ? (this.orderingContext.intention === OrderingContextIntention.VIEW_STORES ? "woa.heading.ordering-context.orderType.stores" : "woa.heading.ordering-context.orderType.query")
                    : this.showDeliveryAddressEntry && this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ? "woa.heading.edit-delivery-address.title"
                        : this.orderingContext.intention === OrderingContextIntention.VIEW_STORES ? (
                                this.orderType === OrderType.ORDER_AHEAD ? "woa.heading.ordering-context.find-store.orderAhead"
                                    : this.orderType === OrderType.ORDER_DELIVERY ? "woa.heading.ordering-context.find-store.delivery"
                                        : this.orderType === OrderType.ORDER_AT_TABLE ? "woa.heading.ordering-context.find-store.orderAtTable"
                                            : "woa.heading.ordering-context.findStore")
                            : this.orderType === OrderType.ORDER_AHEAD ? "woa.heading.ordering-context.select-store.orderAhead"
                                : this.orderType === OrderType.ORDER_DELIVERY ? "woa.heading.ordering-context.select-store.delivery"
                                    : this.orderType === OrderType.ORDER_AT_TABLE ? "woa.heading.ordering-context.select-store.orderAtTable"
                                        : "woa.heading.ordering-context.selectStore.title";
    }

    get noStoresInLocationMessage(): string {
        return this.orderType === OrderType.ORDER_AHEAD ? "woa.message.ordering-context.noOrderAheadStoresInLocation"
            : this.orderType === OrderType.ORDER_DELIVERY ? "woa.message.ordering-context.noOrderDeliveryStoresInLocation"
                : this.orderType === OrderType.ORDER_AT_TABLE ? "woa.message.ordering-context.noOrderAtTableStoresInLocation"
                    : "woa.message.ordering-context.noStoresInLocation";
    }

    get showAddressSearchInHeader(): boolean {
        return (this.orderingContext.intention === OrderingContextIntention.VIEW_STORES && !this.showStoreOrderTypeOptions) ||
            this.orderType === OrderType.ORDER_AHEAD ||
            this.orderType === OrderType.ORDER_AT_TABLE ||
            (this.orderType === OrderType.ORDER_DELIVERY && !this.showDeliveryAddressInHeader && !this.showDeliveryAddressEntry);
    }

    get hasConfirmedDeliveryAddress(): boolean {
        return !this.treatDeliveryAddressAsUnconfirmed && (!!this.orderingContext.selectedDeliveryDetails || this.orderingContextModel.addressTrusted);
    }

    get showDeliveryAddressInHeader(): boolean {
        return this.orderType === OrderType.ORDER_DELIVERY && !this.editingDeliveryAddress && this.hasConfirmedDeliveryAddress;
    }

    get showDeliveryAddressEntry(): boolean {
        return this.orderType === OrderType.ORDER_DELIVERY && !this.deliveryAddressError &&
            (this.editingDeliveryAddress || this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS || (!this.isViewIntention && !this.hasConfirmedDeliveryAddress));
    }

    get formattedDeliveryAddress(): string {
        return this.orderingContext.selectedDeliveryDetails ? this.orderingContext.selectedDeliveryDetails.address.formattedAddress : this.orderingContextModel.address.formattedAddress;
    }

    get deliveryAddressError(): string {
        return this.orderType === OrderType.ORDER_DELIVERY && this.orderingContext.selectedDeliveryDetails ? this.addressError : undefined;
    }

    get showStoreOrderTypeOptions(): boolean {
        return !!this.orderType && this.storeOrderTypeSelectionRequired;
    }

    get showStoreHeader(): boolean {
        return this.showStoreSelection && (this.showAddressSearchInHeader || this.showDeliveryAddressInHeader || !!this.deliveryAddressError || this.showStoreOrderTypeOptions);
    }

    get showNearestStores(): boolean {
        return !this.orderingContextModel.singleStoreLocation && !this.showAllStores && !this.showDeliveryAddressEntry; //  && !this.showDeliveryAddressError;
    }

    get showStateWithName(): boolean {
        return this.showAllStores && (!this.location || !!this.location.countryOnly);
    }

    get showOrderTypeSelection(): boolean {
        return this.orderingContext.intention === OrderingContextIntention.SELECT_ORDER_TYPE || (!this.orderType && this.orderTypeSelectionRequired);
    }

    get showTableSelection(): boolean {
        return !this.showOrderTypeSelection && this.selectingTable;
    }

    get currentStore(): StoreData {
        return this.orderingContext.selectedStore || this.orderingContextModel.currentStore;
    }

    get availableBarcodes(): TableCodeBarcodeData[] {
        return this.currentStore ? this.currentStore.tableCodeBarcodes : this.tableCodeBarcodes;
    }

    get tableLookupRequired(): boolean {
        return !!this.availableBarcodes.length;
    }

    get allowAlphaTableCodes(): boolean {
        return this.availableBarcodes.some((tcb: TableCodeBarcodeData) => !StringUtils.isNumber(tcb.barcode));
    }

    get showStoreSelection(): boolean {
        return !this.showOrderTypeSelection && !this.showTableSelection;
    }

    get showMessageTableOrderingNotAvailableNearestStore(): boolean {
        return this.canOrderAtTable && (!this.storeOrderTypeSelectionRequired || this.orderType === OrderType.ORDER_AT_TABLE) &&
            this.storeSearchModel.nearestStores.length && !this.storeSearchModel.nearestStores[0].orderAtTableStore;
    }

    get nearestStoreName(): string {
        return this.storeSearchModel.nearestStores.length ? this.storeSearchModel.nearestStores[0].name : undefined;
    }

    get collapseStoreText(): boolean {
        return this.responsiveService.mobile && (!this.sessionModel.currentApp.showStoreImagesOnMobileStoreSelection || this.sessionModel.currentApp.showStoreAddrOnStoreSelection);
    }

    get showStoreImage(): boolean {
        return this.responsiveService.mobile ? this.sessionModel.currentApp.showStoreImagesOnMobileStoreSelection : this.sessionModel.currentApp.showStoreImagesOnStoreSelection;
    }

    get deliveryAddressConfirmLabel(): string {
        return !this.hasExistingAddress || this.hasConfirmedDeliveryAddress ? "woa.button.common.continue" : "woa.button.ordering-context.confirmDeliveryAddress";
    }

    get hideTableCodeQrScanner(): boolean {
        return !this.tableCodeScannerExists || !this.tableCodeScanning || this.isNativeCamera || this.tableSelectionMethod !== TableLookupMethod.QR_CODE;
    }

    load(): void {
        this.isLoading = true;
        this.hasLoaded = true;
        this.loadingFailed = false;

        if (this.orderingContext.selectedTable && (this.orderingContext.selectedTable.webTerminalIds || this.orderingContext.selectedTable.storeGroup)) {
            this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
            if (this.orderingContext.selectedTable.webTerminalIds) {
                this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORES, this.orderingContext.selectedTable.webTerminalIds);
            } else {
                this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORE_GROUP, this.orderingContext.selectedTable.storeGroup);
            }
        }
        if (this.orderingContext.selectedOrderType === OrderType.ORDER_AHEAD) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_AHEAD_ONLY, 1);
        } else if (this.orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_DELIVERY_ONLY, 1);
        } else if (this.orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_AT_TABLE_ONLY, 1);
        }

        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.storeSearchModel.retrieve();
                this.checkStoreOptions();
                this.orderTypeChanged();
            } catch (error) {
                console.log("Store load failed:", error);
                this.loadingService.dismissSpinner();
                this.isLoading = false;
                this.loadingFailed = true;
                this.notificationService.logErrorToServer("OrderingContextSelectionPageComponent.load - store load failed", undefined, error);
            }
            if (!this.identityModel.isGuestUser && !this.identityModel.currentMember) {
                await this.identityModel.loadMemberSummary();
            }
        }, 20000, undefined, undefined, false);

        this.initialiseAddress();
        this.clearStoreAndTable();
    }

    private initialiseAddress(): void {
        if (this.orderingContextModel.address) {
            this.street = this.orderingContextModel.address.street;
            this.city = this.orderingContextModel.address.city;
            this.state = this.orderingContextModel.address.state;
            this.postCode = this.orderingContextModel.address.postCode;
            this.deliveryInstructions = this.orderingContextModel.deliveryInstructions;
            this.orderMobilePhone = this.orderingContextModel.mobilePhone;
            if (this.street || this.city) {
                this.hasExistingAddress = true;
            }
        } else {
            this.street = undefined;
            this.city = undefined;
            this.state = undefined;
            this.postCode = undefined;
            this.deliveryInstructions = undefined;
            this.orderMobilePhone = undefined;
            this.hasExistingAddress = false;
        }
        this.deliveryDetailsChanged();
    }

    /**
     * Analyses the available stores to determine which options are available to the user
     */
    checkStoreOptions(): void {
        this.storeSearchModel.determineAvailableStores();

        if (!this.orderingContext.selectedMenuItem && this.orderingContextModel.currentStoreLocation) {
            this.orderAheadStoreOption = this.orderingContextModel.currentStoreLocation.getStoreForOrderType(OrderType.ORDER_AHEAD);
            this.orderDeliveryStoreOption = this.orderingContextModel.currentStoreLocation.getStoreForOrderType(OrderType.ORDER_DELIVERY);
            this.orderAtTableStoreOption = this.orderingContextModel.currentStoreLocation.getStoreForOrderType(OrderType.ORDER_AT_TABLE);
        }

        this.canOrderAhead = (!this.orderingContext.selectedOrderType || this.orderingContext.selectedOrderType === OrderType.ORDER_AHEAD) &&
            (!!this.orderAheadStoreOption || this.storeSearchModel.hasOrderAheadStores$.value);
        this.canOrderDelivery = (!this.orderingContext.selectedOrderType || this.orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) &&
            (!!this.orderDeliveryStoreOption || this.storeSearchModel.hasOrderDeliveryStores$.value);
        this.canOrderAtTable = (!this.orderingContext.selectedOrderType || this.orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) &&
            (!!this.orderAtTableStoreOption || this.storeSearchModel.hasOrderAtTableStores$.value);

        this.canFilterByState = this.orderingContextModel.hasAnyStoresWithState(this.storeSearchModel.data);

        // Determine the combination of ordering options available
        let orderOptions: number = 0;
        if (this.canOrderAhead) {
            orderOptions++;
        }
        if (this.canOrderDelivery) {
            orderOptions++;
        }
        if (this.canOrderAtTable) {
            orderOptions++;
        }

        let defaultToDelivery: boolean = false;

        // Determine whether an order type selection is required, or if each store panel handles the choice between available order types.
        // The intention is to only show the selector when absolutely necessary.

        // Only order type selection required?
        if (this.orderingContext.intention === OrderingContextIntention.SELECT_ORDER_TYPE) {
            this.orderTypeSelectionRequired = true;
        }

        // If there is only one option, then obviously no selection is required.
        else if (orderOptions <= 1) {
            this.storeOrderTypeSelectionRequired = false;
            this.orderTypeSelectionRequired = false;
        }

        // If delivery is an option, the choice of stores may be different based on the confirmed delivery address, or in order mode, address entry may be required.
        else if (this.canOrderDelivery) {
            if (!this.isViewIntention) {
                this.storeOrderTypeSelectionRequired = true;
                this.orderTypeSelectionRequired = true;
            } else if (this.hasConfirmedDeliveryAddress) {
                this.storeOrderTypeSelectionRequired = true;
                this.orderTypeSelectionRequired = false;

                // Need a default selection - can't show the order type selector with nothing selected.
                defaultToDelivery = true;
            } else {
                this.storeOrderTypeSelectionRequired = this.storePersonalitiesUsed();
                this.orderTypeSelectionRequired = this.storeOrderTypeSelectionRequired;
            }
        }

        // Otherwise, if all stores do not support all available ordering types, a selection is required.
        else {
            this.storeOrderTypeSelectionRequired = this.storePersonalitiesUsed();
            this.orderTypeSelectionRequired = this.storeOrderTypeSelectionRequired;
        }

        if (this.orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) {
            if (this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ||
                (!this.isViewIntention && this.hasConfirmedDeliveryAddress)) {
                this.treatDeliveryAddressAsUnconfirmed = true;
            }
            if (this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.storeOrderTypeSelectionRequired = false;
                this.orderTypeSelectionRequired = false;
            }
        } else if (this.orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) {

            // Changing table selection
            if (this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.selectingTable = true;
            }

            // Store has already been selected
            else if (this.orderingContext.selectedStore) {
                this.selectingTable = true;
            }

            // Changing order type (and no preselected table)
            else if (this.orderingContextModel.orderType !== OrderType.ORDER_AT_TABLE && !this.orderingContextModel.preselectedTableNumber) {
                this.selectingTable = true;
            }

            // Neither a current store nor a preselected table exist in the session model (initial table ordering choice)
            else if (!this.orderingContextModel.currentStore && !this.orderingContextModel.preselectedTableNumber) {
                this.selectingTable = true;
            }

            if (this.selectingTable) {
                this.orderTypeSelectionRequired = false;
            }
        }

        // If a default order type is set on the ordering context, use it if order type selection is required.
        // Otherwise, default the order type if there is only one option.
        // Otherwise, if selection is required default it only if already set in the session model.
        if (this.orderingContext.defaultOrderType) {
            if (this.storeOrderTypeSelectionRequired) {
                this.orderType = this.orderingContext.defaultOrderType;
            }
        } else if (!this.orderingContext.selectedOrderType) {
            this.orderType = undefined;
            if (orderOptions == 1) {
                if (this.orderingContextModel.orderType) {
                    this.orderType = this.orderingContextModel.orderType;
                } else if (this.canOrderAhead) {
                    this.orderType = OrderType.ORDER_AHEAD;
                } else if (this.canOrderDelivery) {
                    this.orderType = OrderType.ORDER_DELIVERY;
                } else if (this.canOrderAtTable) {
                    this.orderType = OrderType.ORDER_AT_TABLE;
                }
            } else if (this.storeOrderTypeSelectionRequired) {
                this.orderType = this.orderingContextModel.orderType;

                if (!this.orderType && defaultToDelivery) {
                    this.orderType = OrderType.ORDER_DELIVERY;
                }
            }
        }

        // Gather a list of all available custom tables for table ordering stores
        this.storeSearchModel.data.forEach((store: StoreData) => {
            if (store.orderAtTableStore) {
                this.tableCodeBarcodes.push(...store.tableCodeBarcodes);
            }
        });

        this.storeOptionsChecked = true;
    }

    /**
     * Checks for any stores of the same name at the same location with different ordering types.
     * We do not want to show these in the same list as the user should not perceive them as separate stores.
     */
    private storePersonalitiesUsed(): boolean {
        return this.storeSearchModel.data.some((store: StoreData) => this.storeSearchModel.data.some((otherStore: StoreData) =>
            store !== otherStore && store.name === otherStore.name && store.locationId === otherStore.locationId &&
            (store.orderAheadStore !== otherStore.orderAheadStore || store.orderDelivery !== otherStore.orderDelivery || store.orderAtTableStore !== otherStore.orderAtTableStore)));
    }

    async orderTypeSelected(orderType: OrderType): Promise<any> {
        if (this.orderingContext.intention === OrderingContextIntention.SELECT_ORDER_TYPE && orderType === this.orderingContextModel.orderType) {
            return this.navigationService.dismissPage(this, this.orderingContext);
        }

        let store: StoreData;
        if (this.orderingContext.intention !== OrderingContextIntention.SELECT_STORE) {
            store = orderType === OrderType.ORDER_AHEAD ? this.orderAheadStoreOption
                : orderType === OrderType.ORDER_DELIVERY ? this.orderDeliveryStoreOption
                    : orderType === OrderType.ORDER_AT_TABLE ? this.orderAtTableStoreOption
                        : undefined;
        }
        this.orderingContext = this.orderingContext
            .withSelectedStore(store)
            .withSelectedOrderType(orderType);

        // Nothing more to do for order type selection only or for pickups if the store is known
        if (this.orderingContext.intention === OrderingContextIntention.SELECT_ORDER_TYPE || (orderType === OrderType.ORDER_AHEAD && store)) {
            return this.navigationService.dismissPage(this, this.orderingContext);
        }

        // Store is selected by table code for table ordering rather than explicit selection
        if (orderType === OrderType.ORDER_AT_TABLE && (this.orderingContext.intention === OrderingContextIntention.ORDER || this.orderingContext.intention === OrderingContextIntention.SELECT_STORE)) {
            return this.startSelectFulfilmentDetails(this.orderingContext
                .withSelectedOrderType(OrderType.ORDER_AT_TABLE));
        }

        this.orderType = orderType;
        this.orderTypeChanged();
    }

    changeOrderType(orderType: OrderType): void {
        this.orderType = orderType;
        this.orderTypeChanged();
    }

    orderTypeChanged(): void {
        if (this.orderingContext.intention !== OrderingContextIntention.VIEW_STORES) {
            if (this.orderType === OrderType.ORDER_DELIVERY) {
                this.location = undefined;
            } else if (this.editingDeliveryAddress) {
                this.editingDeliveryAddress = false;
                this.changeDetectorRef.detectChanges();
            }
        }
        // noinspection JSIgnoredPromiseFromCall
        this.findNearestStores();
    }

    async locationSelected(location: LocationSearchCoordinates): Promise<void> {
        this.location = location;
        this.isLoading = true;
        await this.loadingService.performWithSpinner(async () => await this.findNearestStores());
    }

    editDeliveryAddress(event?: Event): void {
        event && event.stopPropagation();
        if (this.orderingContext.selectedDeliveryDetails) {
            this.addressError = undefined;
        }
        this.editingDeliveryAddress = true;
        this.changeDetectorRef.detectChanges();
    }

    async clearDeliveryAddress(): Promise<void> {
        this.orderingContext = this.orderingContext.withSelectedDeliveryDetails(new DeliveryDetails());
        this.orderingContextModel.deliveryStores = null;
        this.initialiseAddress();
        this.form.markAsUnsubmitted();
        this.checkStoreOptions();
        await this.loadingService.performWithSpinner(async () => await this.findNearestStores());
    }

    async confirmDeliveryAddress(): Promise<void> {
        if (!this.form.valid) {
            return;
        }

        if (!this.geocoder) {
            this.geocoder = new google.maps.Geocoder();
        }
        this.geocoder.geocode({
            address: this.street + ", " + this.city + ", " + this.state + (this.postCode ? (" " + this.postCode) : "")
        }, async (responses, status) => {
            if (status !== GeocoderStatus.OK) {
                return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
            }
            const geoResult: GeocoderResult = responses[0];
            const latitude = geoResult.geometry.location.lat();
            const longitude = geoResult.geometry.location.lng();
            console.log("Address lookup result:", geoResult, latitude, longitude);
            if (geoResult.address_components.find((value: GeocoderAddressComponent) => value.types.find((value1: string) => value1 == "locality") != undefined) == undefined) {
                return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
            }

            this.editingDeliveryAddress = this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS;
            this.treatDeliveryAddressAsUnconfirmed = this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS;
            this.isLoading = this.orderingContext.intention !== OrderingContextIntention.CHANGE_FULFILMENT_DETAILS;

            this.orderingContext = this.orderingContext.withSelectedDeliveryDetails(new DeliveryDetails(
                this.street, this.city, this.state, this.postCode, this.orderMobilePhone,
                this.deliveryInstructions, this.saveForMemberSpecified && this.saveForMember, latitude, longitude));
            this.orderingContextModel.deliveryStores = null;

            this.changeDetectorRef.detectChanges();
            if (!await this.loadingService.performWithSpinner(async () => await this.findNearestStores())) {
                this.editingDeliveryAddress = false;
                this.treatDeliveryAddressAsUnconfirmed = false;
                this.changeDetectorRef.detectChanges();
                return;
            }

            // If a delivery store has already been pre-selected, we can immediately proceed with ordering using that store
            if (this.orderingContext.selectedStore && (this.orderingContext.intention === OrderingContextIntention.ORDER || this.orderingContext.intention === OrderingContextIntention.SELECT_ORDER_TYPE)) {
                this.loadingService.dismissSpinner();
                // noinspection ES6MissingAwait
                this.selectContext(this.orderingContext.selectedStore, OrderType.ORDER_DELIVERY);
            } else if (this.orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.orderingContextModel.applyDeliveryDetails(this.orderingContext.selectedDeliveryDetails);
                return this.navigationService.dismissPage(this);
            }
            this.changeDetectorRef.detectChanges();
        });
    }

    deliveryDetailsChanged(): void {
        if (this.identityModel.isGuestUser) {
            return;
        }
        if (!this.identityModel.currentIdentity.id || !this.identityModel.currentIdentity.homeAddress) {
            this.addressMatchesMember = false;
        } else {
            this.addressMatchesMember =
                this.identityModel.currentIdentity.homeAddress.street === this.street &&
                this.identityModel.currentIdentity.homeAddress.city === this.city &&
                this.identityModel.currentIdentity.homeAddress.state === this.state &&
                this.identityModel.currentIdentity.homeAddress.postCode === this.postCode &&
                this.identityModel.currentIdentity.mobilePhone === this.orderMobilePhone &&
                this.identityModel.currentIdentity.deliveryInstructions === this.deliveryInstructions;
        }
        if (this.addressMatchesMember) {
            // reset - address is "implicitly saved"
            this.saveForMember = true;
            this.saveForMemberSpecified = false;
        } else if (!this.saveForMemberSpecified) {
            // they need to opt in to saving it when they change from the default
            this.saveForMember = this.orderingContextModel.saveAddressForMember;
            if (this.saveForMember) {
                this.saveForMemberSpecified = true;
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    saveDeliveryAddressForMemberChanged(): void {
        this.saveForMemberSpecified = true;
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Reloads the nearest stores data based on the order type.
     * @returns false if this is a delivery order and the confirmed address either has no valid delivery store, or is invalid for the current store (if any); true otherwise
     */
    async findNearestStores(): Promise<boolean> {
        this.showAllStores = false;

        try {
            if (this.orderType !== OrderType.ORDER_DELIVERY) {

                // If a location has been specified, base the nearest stores on that location.
                if (this.location) {

                    // If the location only specifies a state, then the lat/long values are simply the geographic centre of the state.
                    // Since this is not particularly useful, we instead show all store in the specified state, ignoring store range and nearest store limits.
                    if (this.location.stateOnly || this.location.countryOnly) {
                        this.showAllStores = true;
                        this.storeSearchModel.clearNearestStores();
                    } else {
                        this.storeSearchModel.findNearestStores(
                            this.sessionModel.currentApp.storeLimit, this.sessionModel.currentApp.storeSearchRange, this.sessionModel.currentApp.deliveryStoreRange,
                            this.location.lat, this.location.lng);
                    }
                }

                // If no location has been specified, attempt to use the device's current location. If this fails, then we will show all stores grouped by state.
                else if (this.orderingContextModel.disableLocationServices) {
                    this.showAllStores = true;
                    this.storeSearchModel.clearNearestStores();
                } else {
                    try {
                        if (!this.position) {
                            this.position = await this.geolocateService.getCurrentLocation(5000);
                        }
                        this.storeSearchModel.findNearestStores(
                            this.sessionModel.currentApp.storeLimit, this.sessionModel.currentApp.storeSearchRange, this.sessionModel.currentApp.deliveryStoreRange,
                            this.position.coords.latitude, this.position.coords.longitude);
                    } catch (error) {
                        this.showAllStores = true;
                        this.storeSearchModel.clearNearestStores();
                    }
                }
            } else if (this.orderingContext.selectedDeliveryDetails) {
                this.addressError = undefined;

                // Delivery stores are always based on the delivery address, and are cached in the session model to avoid the
                // repeated overhead and cost of a distance by road search.
                if (!this.orderingContextModel.deliveryStores) {
                    this.addressError = await this.orderingContextModel.findDeliveryStores(this.storeSearchModel, this.orderingContext.selectedDeliveryDetails);
                    if (this.addressError) {
                        return false;
                    }
                }

                // Validate the current/selected store against the address
                const store: StoreData = this.orderingContext.selectedStore || this.orderingContextModel.currentStore;
                if (store) {
                    if (!this.orderingContextModel.deliveryStores.find((deliveryStore: StoreData) => deliveryStore.webTerminalId === store.webTerminalId)) {
                        this.addressError = "woa.message.ordering-context.wrongStoreForDeliveryAddress";
                        return false;
                    }
                    if (store.integratedDelivery && !await this.validateIntegratedDeliveryAddress(store, this.orderingContext.selectedDeliveryDetails.address)) {
                        return false;
                    }
                }
            }

            return true;
        } finally {
            this.loadingService.dismissSpinner();
            this.changeDetectorRef.detectChanges();
            this.isLoading = false;
            this.loadingFailed = false;
        }
    }

    async validateIntegratedDeliveryAddress(store: StoreData, address: AddressData): Promise<boolean> {
        return this.loadingService.performWithSpinner(async () => {
            const integratedDeliveryAddressValidationRequest: IntegratedDeliveryAddressValidationRequestData = <IntegratedDeliveryAddressValidationRequestData>EntityData.create(IntegratedDeliveryAddressValidationRequestData);
            integratedDeliveryAddressValidationRequest.webTerminalId = store.webTerminalId;
            integratedDeliveryAddressValidationRequest.address = address;
            integratedDeliveryAddressValidationRequest.expectedCompletion = this.orderingContextModel.expectedCompletion;
            const result = await this.integratedDeliveryAddressValidationService.performAction(integratedDeliveryAddressValidationRequest);
            const valid: boolean = result["valid"];
            if (!valid) {
                this.addressError = this.orderingContextModel.singleStoreLocation ? "woa.message.ordering-context.integratedDeliveryUnavailableSingleStore" : "woa.message.ordering-context.integratedDeliveryUnavailable";
                this.addressErrorStore = store;
            }
            return valid;
        }, 20000);
    }

    filterStores(stores: StoreData[]): StoreData[] {
        return this.showDeliveryAddressInHeader ? this.orderingContextModel.deliveryStores
            : stores.filter((store: StoreData) =>
                !(this.orderType && !store.canOrder(this.orderType)) &&
                !(this.canFilterByState && this.location && this.location.stateOnly && this.stateName(store.state) !== this.stateName(this.location.stateOnly)));
    }

    stateName(alt: string): string {
        const stateNames: string[] = OrderingContextSelectionPageComponent.stateNames.find((alts: string[]) => alts.indexOf(alt.toLowerCase()) > -1);
        return stateNames ? stateNames[0] : undefined;
    }

    trackStoreByFn(index, store: StoreData) {
        return store.webTerminalId;
    }

    trackGroupStoreByFn(index, stateStore) {
        return stateStore.key;
    }

    async showStoreDetails(store: StoreData): Promise<void> {
        const orderingContext: OrderingContext = await this.navigationService.createPage("woa-store-details-page", {
            orderingContext: this.orderingContext
                .withSelectedStore(store)
                .withSelectedOrderType(this.orderType)
        });
        if (orderingContext) {
            return this.selectContext(orderingContext.selectedStore, orderingContext.selectedOrderType);
        }
    }

    async selectContext(store: StoreData, orderType: OrderType, table?: TableSelection): Promise<any> {
        // Note: if the ordering context has a selected table, the table number will be applied to the store
        // when the selection context is applied to the session context (see SessionModel.applyStoreSelection() and SessionModel.set currentStore()).
        // That will also take care of resolving custom mappings for the table number against the selected store, or assigning the default naming.

        if (orderType === OrderType.ORDER_AT_TABLE && !this.orderingContext.selectedTable && !table) {
            return this.startSelectFulfilmentDetails(this.orderingContext
                .withIntention(OrderingContextIntention.ORDER)
                .withSelectedStore(store)
                .withSelectedOrderType(orderType));
        }
        if (orderType === OrderType.ORDER_DELIVERY) {
            if (!this.hasConfirmedDeliveryAddress) {
                return this.startSelectFulfilmentDetails(this.orderingContext
                    .withIntention(OrderingContextIntention.ORDER)
                    .withSelectedStore(store)
                    .withSelectedOrderType(orderType));
            }
            if (store.integratedDelivery) {
                const address: AddressData = this.orderingContext.selectedDeliveryDetails ? this.orderingContext.selectedDeliveryDetails.address : this.orderingContextModel.address;
                if (!await this.validateIntegratedDeliveryAddress(store, address)) {
                    this.changeDetectorRef.detectChanges();
                    return;
                }
            }
        }
        return this.navigationService.dismissPage(this, this.orderingContext
            .withSelectedStore(store)
            .withSelectedOrderType(orderType)
            .withSelectedTable(table || this.orderingContext.selectedTable));
    }

    private async startSelectFulfilmentDetails(orderingContext: OrderingContext): Promise<void> {
        this.orderingContext = orderingContext;
        this.orderType = orderingContext.selectedOrderType;
        if (orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) {
            this.selectingTable = true;
            await this.initTableCodeScanner();
        } else if (orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) {
            this.editingDeliveryAddress = true;
        }
    }

    private async initTableCodeScanner(): Promise<void> {
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists && !this.tableCodeScannerAuthorised) {
            await this.prepareTableCodeScanner();
        }
        if (this.sessionModel.waiterMode && this.nativeWaiterAppService.isAvailable()) {
            setTimeout(() => this.startTableCodeScan(), 50);
        }
    }

    private async chooseTableCodeScanner(): Promise<void> {
        if (this.tableCodeScannerChosen) {
            return;
        }
        this.tableSelectionMethod = this.sessionModel.effectiveTableLookupMethod;
        if (this.tableSelectionMethod === TableLookupMethod.MANUAL) {
            this.tableCodeScannerExists = false;
        } else if (this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            this.tableCodeScannerExists = await this.qrCodeReaderService.exists();

            // For QR, we fall back to manual entry
            if (!this.tableCodeScannerExists) {
                this.tableSelectionMethod = TableLookupMethod.MANUAL;
            }
        }

        this.isNativeCamera = await this.qrCodeReaderService.isNativeCamera();

        this.tableCodeScannerChosen = true;
    }

    private setTableCodeScanning(scanning: boolean): void {
        this.tableCodeScanning = scanning;
        if (this.tableCodeScanning && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            document.body.classList.add("scanning");
        } else {
            document.body.classList.remove("scanning");
        }
        this.changeDetectorRef.detectChanges();
    }

    private setTableCodeScannerMsg(msg: string): void {
        this.tableCodeScannerMsg = msg;
        this.changeDetectorRef.detectChanges();
    }

    setTableCodeLookupCodeMsg(msg: string): void {
        this.tableLookupCodeMsg = msg;
        this.changeDetectorRef.detectChanges();
    }

    private async prepareTableCodeScanner(scanIfAuthorised: boolean = false): Promise<void> {
        await this.chooseTableCodeScanner();
        this.setTableCodeLookupCodeMsg(undefined);
        this.setTableCodeScannerMsg(undefined);
        if (!this.tableCodeScannerExists) {
            return;
        }

        if (this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            try {
                const status: ScannerStatus = await this.qrCodeReaderService.prepare(this.elementRef, this.containerQrCode);
                console.log("QRScanner.prepare status: ", status);
                this.handleTableCodeScannerStatus(status, scanIfAuthorised);
                this.canOpenSettings = status.canOpenSettings;
            } catch (error) {
                console.log("QRScanner.prepare error: ", error);
            }
        }
    }

    private handleTableCodeScannerStatus(status: any, scanIfAuthorised: boolean): void {
        this.tableCodeScannerAuthorised = false;
        this.tableCodeScannerDenied = false;
        if (status.authorized) {
            // camera permission was granted
            this.tableCodeScannerAuthorised = true;
            if (scanIfAuthorised) {
                // noinspection JSIgnoredPromiseFromCall
                this.startTableCodeScan();
            }
        } else if (status.denied) {
            // camera permission was permanently denied
            this.tableCodeScannerDenied = true;
            this.setTableCodeScannerMsg("woa.error.ordering-context.scanDenied");
        } else {
            // permission was denied, but not permanently. You can ask for permission again at a later time.
            this.setTableCodeScannerMsg("woa.error.ordering-context.scanNotAllowed");
        }
    }

    private async hideTableCodeScanner(): Promise<void> {
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists) {
            this.setTableCodeScanning(false);

            if (this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
                await this.qrCodeReaderService.hide();
            }
        }
    }

    private async pauseTableCodeScanner(): Promise<void> {
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.qrCodeReaderService.pausePreview();
        }
    }

    private async resumeTableCodeScanner(): Promise<void> {
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.qrCodeReaderService.resumePreview();
        }

    }

    private async cancelTableCodeScanner(): Promise<void> {
        await this.chooseTableCodeScanner();
        this.setTableCodeScanning(false);
        if (this.tableCodeScannerExists && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.qrCodeReaderService.cancelScan();
        }
    }

    private async destroyTableCodeScanner(): Promise<void> {
        this.tableCodeScannerAuthorised = false;
        await this.hideTableCodeScanner();
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.qrCodeReaderService.destroy();
        }
    }

    async enableTableCodeCamera(): Promise<void> {
        await this.chooseTableCodeScanner();
        if (this.tableCodeScannerExists && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.qrCodeReaderService.openSettings();
        }
    }

    async startTableCodeScan(): Promise<void> {
        this.manualTableCode = undefined;
        this.tableLookupCode = undefined;
        this.clearStoreAndTable();
        this.setTableCodeLookupCodeMsg(undefined);
        this.setTableCodeScannerMsg(undefined);
        if (!this.tableCodeScannerAuthorised && this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            await this.prepareTableCodeScanner(true);
            return;
        }

        this.setTableCodeScanning(true);
        await this.resumeTableCodeScanner();

        if (this.tableSelectionMethod === TableLookupMethod.QR_CODE) {
            try {
                const scannedCode: string = await this.qrCodeReaderService.scan();
                // noinspection ES6MissingAwait
                this.pauseTableCodeScanner();
                setTimeout(() => this.lookupTable(scannedCode), 500);
            } catch (error) {
                console.log("QRScanner.scan error: ", error);
                if (error.name === "NOT_AUTHORISED") {
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.ordering-context.scanDenied"));
                    this.setTableCodeScanning(false);
                } else if (error.name === "SCAN_CANCELED") {
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.ordering-context.scanCancelled"));
                    this.setTableCodeScanning(false);
                } else {
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.ordering-context.scanFailure"));
                    console.log(error._message || error);
                    this.setTableCodeScanning(false);
                }
            }
        }
    }

    stopTableCodeScan(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.hideTableCodeScanner();
        // noinspection JSIgnoredPromiseFromCall
        this.cancelTableCodeScanner();
    }

    async acceptTableCode(): Promise<void> {
        if (!this.form.valid) {
            return;
        }
        return this.lookupTable(this.manualTableCode, this.sessionModel.currentApp.onlineManualTableLookup);
    }

    private clearStoreAndTable(): void {
        this.selectedStoreForTable = undefined;
        this.selectedTable = undefined;
        this.changeDetectorRef.detectChanges();
    }

    private selectStoreAndTable(store: StoreData, orderType: OrderType, table: TableSelection): void {
        this.selectedStoreForTable = store;
        this.orderType = orderType;
        this.selectedTable = table;
        if (store) {
            // noinspection JSIgnoredPromiseFromCall
            this.selectContext(store, orderType, table);
        } else {
            this.selectingTable = false;
            this.orderingContext = this.orderingContext
                .withSelectedOrderType(orderType)
                .withSelectedTable(table);
            this.changeDetectorRef.detectChanges();
        }
    }

    async lookupTable(scannedText: string, useLookupService: boolean = true): Promise<void> {
        console.log("Scanned text: " + scannedText);
        console.log("Configured to use lookup service? " + useLookupService);

        this.clearStoreAndTable();
        this.setTableCodeLookupCodeMsg(undefined);

        await this.loadingService.performWithSpinner(async () => {

            // Analyse scanned text
            this.tableLookupCode = undefined;
            let scannedGuid: string;
            let scannedTableNumber: string;
            let tableLookupResponse: TableLookupResponse;

            let scannedUrl: string = undefined;
            if (scannedText.startsWith("http") && scannedText.indexOf("?") >= 0) {
                const urlParams: URLSearchParams = new URLSearchParams(scannedText.substring(scannedText.indexOf("?")));
                scannedGuid = urlParams.get("id");
                scannedTableNumber = urlParams.get("tn");
                const tableLookupUrl: string = await this.tableLookupService.getTableLookupUrl();

                // If the scanned value matches the table lookup service URL, pass it directly to the lookup service
                if (scannedText.startsWith(tableLookupUrl)) {
                    scannedUrl = scannedText;
                }

                // Otherwise, if the scanned value is a URL with parameters that look like a table lookup, assume the URL has changed or been locally overridden, and rebuild it with the current URL
                else if (scannedGuid) {
                    const url = new URL(tableLookupUrl + "/r");
                    url.searchParams.append("id", scannedGuid);
                    if (scannedTableNumber) {
                        url.searchParams.append("tn", scannedTableNumber);
                    }
                    scannedUrl = url.href;
                }

                // Otherwise, look for a lookupCode URL parameter for a local lookup (will default to a local lookup of the whole scanned value)
                else {
                    this.tableLookupCode = urlParams.get("lookupCode");
                }
            }

            // If the scanned value is not a URL, it may be a "canned" TableLookupResponse
            else if (scannedText.startsWith("rsp:")) {
                console.log(atob(scannedText.substring(4)));
                tableLookupResponse = Object.assign(new TableLookupResponse(), JSON.parse(atob(scannedText.substring(4))));
            }

            // Call Chewzie table lookup service or use canned response
            if (scannedUrl || tableLookupResponse) {
                if (useLookupService || tableLookupResponse) {
                    try {
                        if (!tableLookupResponse) {
                            tableLookupResponse = await this.tableLookupService.lookupTable(scannedUrl);
                            console.log("Table lookup response", tableLookupResponse);
                        }

                        // If the scanned URL redirects outside this app, then simply follow the redirect
                        if (!this.responsiveService.isApp) {
                            const redirectURL: string = tableLookupResponse.redirectURL;
                            if (redirectURL) {
                                let redirectURLBase: string = redirectURL.split("?")[0];
                                if (redirectURLBase.endsWith("/")) {
                                    redirectURLBase = redirectURLBase.substring(0, redirectURLBase.length - 1);
                                }
                                let myURLBase: string = window.location.href.split("?")[0];
                                if (myURLBase.endsWith("/")) {
                                    myURLBase = myURLBase.substring(0, myURLBase.length - 1);
                                }
                                if (redirectURLBase !== myURLBase) {
                                    if (this.nativeWaiterAppService.isAvailable()) {
                                        this.nativeWaiterAppService.redirectToUrl(redirectURL);
                                    } else {
                                        this.navigationService.redirectToUrl(redirectURL);
                                    }
                                    return;
                                }
                            }
                        }

                        // local beacon lookup
                        if (tableLookupResponse.id) {
                            const beacon: BeaconData = await this.beaconSearchModel.lookupBeacon(tableLookupResponse.id);
                            if (beacon) {
                                tableLookupResponse.exclusiveStores = beacon.appStore ? [beacon.appStore] : undefined;
                                tableLookupResponse.exclusiveStoreGroup = beacon.appStoreGroup;
                                tableLookupResponse.tableNumber = beacon.tableNumber;
                                tableLookupResponse.tableOnlyMode = beacon.orderType === BeaconOrderType.TABLE_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                                tableLookupResponse.pickupOnlyMode = beacon.orderType === BeaconOrderType.PICKUP_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                            } else {
                                const msg: string = "Scanned beacon id " + tableLookupResponse.id + " which is not registered for this app";
                                console.log("lookupTable: unknown beacon - " + msg);
                                this.setTableCodeLookupCodeMsg("woa.error.ordering-context.unrecognisedBeacon");
                                this.isLoading = false;
                                // noinspection ES6MissingAwait
                                this.hideTableCodeScanner();
                                return;
                            }
                        }

                        const storeGroup: string = tableLookupResponse.storeGroup || tableLookupResponse.exclusiveStoreGroup;
                        const webTerminalIds: number[] = tableLookupResponse.stores || tableLookupResponse.exclusiveStores;

                        if (!storeGroup && (!webTerminalIds || webTerminalIds.length === 0)) {
                            console.log("lookupTable: no table registered for scanned url: " + scannedUrl);

                            // Fallback to custom table lookup using guid as lookup code
                            scannedUrl = undefined;
                            tableLookupResponse = undefined;
                            this.tableLookupCode = scannedGuid || scannedTableNumber;
                        }

                        // "restaurant" case - single store for table
                        else if (webTerminalIds && webTerminalIds.length === 1) {
                            const webTerminalId: number = webTerminalIds[0];

                            // If a store has been selected (in the current ordering context), the store for the scanned table must match that store.
                            // In other words, if the user selects "order at table from here" for a store in the store list, they can't then scan a code for a different store.
                            if (this.orderingContext.selectedStore && this.orderingContext.selectedStore.webTerminalId !== webTerminalId) {
                                const msg: string = "Expecting web terminal " + this.orderingContext.selectedStore.webTerminalId + " but lookup code " + this.tableLookupCode + " is for web terminal " + webTerminalId;
                                console.log("lookupTable: wrong store - " + msg);
                                this.setTableCodeLookupCodeMsg("woa.error.ordering-context.wrongStore");
                                this.isLoading = false;
                                // noinspection ES6MissingAwait
                                this.hideTableCodeScanner();
                                return;
                            }

                            if (tableLookupResponse.tableNumber) {

                                // Lookup custom table number
                                const lookupStore: StoreData = await this.orderingContextModel.lookupCustomTableNumber(tableLookupResponse.tableNumber, undefined, webTerminalId);
                                if (lookupStore && lookupStore.tableNumber) {
                                    this.selectStoreAndTable(lookupStore, OrderType.ORDER_AT_TABLE, {
                                        storeGroup: storeGroup,
                                        webTerminalIds: webTerminalIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                } else {
                                    try {
                                        let store: StoreData = await this.findStore(webTerminalId);
                                        if (store) {

                                            // Apply the default table prefix since there was no custom mapping found
                                            const tableStore: StoreData = <StoreData>EntityData.create(StoreData).assign(store);
                                            tableStore.tableNumber = await this.orderingContextModel.getDefaultTableName(tableLookupResponse.tableNumber);
                                            this.selectStoreAndTable(tableStore, OrderType.ORDER_AT_TABLE, {
                                                lookupCode: tableLookupResponse.tableNumber,
                                                tableNumber: tableStore.tableNumber,
                                                storeGroup: storeGroup,
                                                webTerminalIds: webTerminalIds,
                                                exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                                tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                                pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                            });
                                        } else {
                                            console.log("lookupTable: no store for web terminal id " + webTerminalId);

                                            // Fallback to custom table lookup using guid as lookup code
                                            scannedUrl = undefined;
                                            tableLookupResponse = undefined;
                                            this.tableLookupCode = scannedGuid || scannedTableNumber;
                                        }
                                    } catch (error) {
                                        this.loadingFailed = true;
                                        this.isLoading = false;
                                        // noinspection ES6MissingAwait
                                        this.hideTableCodeScanner();
                                        return;
                                    }
                                }
                            }

                            // No table number => revert to pickup
                            else {
                                try {
                                    let store: StoreData = await this.findStore(webTerminalId);
                                    if (store) {
                                        const pickupStore: StoreData = <StoreData>EntityData.create(StoreData).assign(store);
                                        this.selectStoreAndTable(pickupStore, OrderType.ORDER_AHEAD, {
                                            exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                            storeGroup: tableLookupResponse.exclusiveStoreGroup,
                                            webTerminalIds: tableLookupResponse.exclusiveStores,
                                            tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                            pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                        });
                                    } else {
                                        console.log("lookupTable: no store");
                                        this.setTableCodeLookupCodeMsg("woa.error.ordering-context.noMatchingLookupCode");
                                    }
                                } catch (error) {
                                    this.loadingFailed = true;
                                    this.isLoading = false;
                                    // noinspection ES6MissingAwait
                                    this.hideTableCodeScanner();
                                    return;
                                }
                            }
                        }

                        // "food court" case - store group or multiple stores with shared tables
                        else if (storeGroup || (webTerminalIds && webTerminalIds.length > 1)) {
                            let tableNumber: string = tableLookupResponse.tableNumber;
                            if (!await this.selectPreselectedStoreWithTableCode(storeGroup, webTerminalIds, tableNumber, null,
                                !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                tableLookupResponse.tableOnlyMode, tableLookupResponse.pickupOnlyMode)) {

                                if (tableNumber) {
                                    // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                                    // we assume for now that they are likely to be the same and that any mapping is better than none.
                                    // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                                    const lookupStore: StoreData = await this.orderingContextModel.lookupCustomTableNumber(tableNumber, storeGroup, storeGroup ? undefined : webTerminalIds[0],
                                        true, true);
                                    if (lookupStore && lookupStore.tableNumber) {
                                        console.log("lookupTable: candidate store found");
                                        tableNumber = lookupStore.tableNumber;
                                    } else {
                                        tableNumber = await this.orderingContextModel.getDefaultTableName(tableNumber);
                                    }
                                    this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, {
                                        lookupCode: tableLookupResponse.tableNumber,
                                        tableNumber: tableNumber,
                                        storeGroup: storeGroup,
                                        webTerminalIds: webTerminalIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                }

                                // No table number => revert to pickup
                                else {
                                    this.selectStoreAndTable(undefined, OrderType.ORDER_AHEAD, {
                                        storeGroup: storeGroup,
                                        webTerminalIds: webTerminalIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                }
                            }
                        }
                    } catch (error) {
                        console.log("lookupTable: Fedelta table lookup service failed for scanned url: " + scannedUrl, error);

                        // Fallback to custom table lookup using guid as lookup code
                        scannedUrl = undefined;
                        tableLookupResponse = undefined;
                        this.tableLookupCode = scannedGuid || scannedTableNumber;
                    }
                    this.isLoading = false;
                    // noinspection ES6MissingAwait
                    this.hideTableCodeScanner();
                } else {
                    scannedUrl = undefined;
                    tableLookupResponse = undefined;
                    this.tableLookupCode = scannedGuid || scannedTableNumber;
                }
            } else {
                console.log("Not using lookup service for this text");
            }

            // Or do custom table lookup
            if (!scannedUrl && !tableLookupResponse) {
                if (!this.tableLookupCode) {
                    this.tableLookupCode = scannedText;
                }

                const paramConfig: ParamConfig = await this.paramConfig.getConfig();

                // Table lookup is not required if there are no custom tables defined
                // (either for the current store if known, or otherwise for any store).
                if (this.tableLookupRequired) {
                    this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
                    this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_LOOKUP_CODE, this.tableLookupCode);
                    this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME, this.orderingContextModel.expectedCompletionAsString);
                    try {
                        await this.storeSearchModel.retrieve();

                        // noinspection ES6MissingAwait
                        this.hideTableCodeScanner();
                        this.loadingService.dismissSpinner();
                        this.isLoading = false;

                        if (this.storeSearchModel.data.length === 0) {
                            console.log("lookupTable: no match");
                            this.setTableCodeLookupCodeMsg("woa.error.ordering-context.noMatchingLookupCode");
                        }

                        // "restaurant" case - single store for table
                        else if (this.storeSearchModel.data.length === 1) {
                            const store: StoreData = this.storeSearchModel.data[0];

                            // If a store has been selected (in the current ordering context), the store for the scanned table must match that store.
                            // In other words, if the user selects "order at table from here" for a store in the store list, they can't then scan a code for a different store.
                            if (this.orderingContext.selectedStore && this.orderingContext.selectedStore.webTerminalId !== store.webTerminalId) {
                                const msg: string = "Expecting " + this.orderingContext.selectedStore.webTerminalName + " but lookup code " + this.tableLookupCode + " is for " + store.webTerminalName;
                                console.log("lookupTable: wrong store - " + msg);
                                this.setTableCodeLookupCodeMsg("woa.error.ordering-context.wrongStore");
                                return;
                            }

                            this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                                tableOnlyMode: true,
                                pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                                deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                                localTable: true
                            });
                        }

                        // "food court" case - multiple stores with shared tables
                        else {
                            const webTerminalIds: number[] = this.storeSearchModel.data.map((store: StoreData) => store.webTerminalId);
                            if (!await this.selectPreselectedStoreWithTableCode(undefined, webTerminalIds, this.tableLookupCode, this.storeSearchModel.data)) {

                                // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                                // we assume for now that they are likely to be the same and that any mapping is better than none.
                                // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                                this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, {
                                    lookupCode: this.tableLookupCode,
                                    tableNumber: this.storeSearchModel.data[0].tableNumber,
                                    webTerminalIds: webTerminalIds,
                                    tableOnlyMode: true,
                                    pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                                    deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                                    localTable: true
                                });
                            }
                        }
                    } catch (error) {
                        console.log("lookupTable: store search error", error);
                        this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
                        this.loadingFailed = true;
                        this.isLoading = false;
                        // noinspection ES6MissingAwait
                        this.hideTableCodeScanner();
                    }
                } else {
                    const store: StoreData = this.currentStore ? <StoreData>EntityData.create(StoreData).assign(this.currentStore) : undefined;
                    if (store) {
                        store.tableCode = undefined;
                        store.tableNumber = undefined;
                    }
                    this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                        lookupCode: this.tableLookupCode,
                        tableNumber: this.tableLookupCode,
                        tableOnlyMode: true,
                        pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                        deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                        localTable: true
                    });
                }
            }
        }, 8000);
    }

    /**
     * Try to find the specified store in the ones we already have loaded in the session model.
     * If it's not there, do a store search ignoring the exclusive filters.
     * @param webTerminalId
     */
    private async findStore(webTerminalId: number): Promise<StoreData> {
        let store: StoreData = this.orderingContextModel.stores.find((store: StoreData) => store.webTerminalId === webTerminalId);
        if (store) {
            return store;
        }
        this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
        this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORES, [webTerminalId]);
        this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME, this.orderingContextModel.expectedCompletionAsString);
        try {
            await this.storeSearchModel.retrieve();
            return (this.storeSearchModel.data && this.storeSearchModel.data.length) ? this.storeSearchModel.data[0] : undefined;
        } catch (error) {
            console.log("lookupTable: store search error. webTerminalId: " + webTerminalId, error);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            throw error;
        }
    }

    private async selectPreselectedStoreWithTableCode(storeGroup: string, webTerminalIds: number[], tableNumber: string, customTableStores?: StoreData[], exclusiveStores?: boolean, tableOnlyMode?: boolean, pickupOnlyMode?: boolean): Promise<boolean> {

        // If a store has been selected in the ordering context, the store group or one of the stores for the scanned code must match that store.
        let preselectedStore: StoreData = this.orderingContext.selectedStore;
        if (preselectedStore) {
            if (storeGroup) {
                if (preselectedStore.storeGroup !== storeGroup) {
                    const msg: string = "Expecting " + this.orderingContext.selectedStore.webTerminalName + " but lookup code " + this.tableLookupCode + " is for store group " + storeGroup;
                    console.log("lookupTable: wrong store - " + msg);
                    this.setTableCodeLookupCodeMsg("woa.error.ordering-context.wrongStore");
                    return false;
                }
            } else if (webTerminalIds) {
                if (webTerminalIds.indexOf(preselectedStore.webTerminalId) === -1) {
                    const msg: string = "Expecting " + this.orderingContext.selectedStore.webTerminalName + " but lookup code " + this.tableLookupCode + " is for multiple other stores";
                    console.log("lookupTable: wrong store - " + msg);
                    this.setTableCodeLookupCodeMsg("woa.error.ordering-context.wrongStore");
                    return false;
                }
            }
        }

        // If a store has already been selected in the session model we want to retain it if it matches the scanned code
        if (!preselectedStore && this.orderingContextModel.currentStore) {
            if (storeGroup) {
                if (this.orderingContextModel.currentStore.storeGroup === storeGroup) {
                    preselectedStore = this.orderingContextModel.currentStore;
                }
            } else if (webTerminalIds) {
                if (webTerminalIds.indexOf(this.orderingContextModel.currentStore.webTerminalId) > -1) {
                    preselectedStore = this.orderingContextModel.currentStore;
                }
            }
        }

        if (!preselectedStore) {
            return false;
        }

        if (tableNumber) {

            // If we already have the mappings from a custom table lookup, look for a custom mapping for the table number on the preselected store.
            //
            // Otherwise, we select a copy of the preselected store, but we don't set the table number on the store at this point.
            // Instead, we capture the selected table information in the ordering context.
            // When the selection context is applied to the session model (see SessionModel.applyStoreSelection() and SessionModel.setCurrentStore()),
            // it will take care of resolving custom mappings for the table number against the selected store, or assigning the default naming.

            let store: StoreData;
            if (customTableStores) {
                store = customTableStores.find((store: StoreData) => store.webTerminalId === preselectedStore.webTerminalId);
            }
            if (!store) {
                store = <StoreData>EntityData.create(StoreData).assign(preselectedStore);
                store.tableNumber = undefined;
            }

            // We already know the store, but we want to retain the original scanned lookup code to allow remapping when swapping between stores sharing the table.
            this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                lookupCode: this.tableLookupCode || tableNumber,
                tableNumber: tableNumber,
                storeGroup: storeGroup,
                webTerminalIds: webTerminalIds,
                exclusiveStores: exclusiveStores,
                tableOnlyMode: tableOnlyMode,
                pickupOnlyMode: pickupOnlyMode
            });
        }

        // No table number => revert to pickup
        else {
            const store: StoreData = <StoreData>EntityData.create(StoreData).assign(preselectedStore);
            this.selectStoreAndTable(store, OrderType.ORDER_AHEAD, {
                storeGroup: storeGroup,
                webTerminalIds: webTerminalIds,
                exclusiveStores: exclusiveStores,
                tableOnlyMode: tableOnlyMode,
                pickupOnlyMode: pickupOnlyMode
            });
        }

        return true;
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-context/ordering-context-selection-page/ordering-context-selection-page.component.ts